/**
 * ticketStatuses.ts — canonical ticket status enum (frontend side).
 *
 * Owns: the 8-value canonical status set, lifecycle order, display labels,
 *       active/terminal partition, and the color hint each picker / pill /
 *       chip in the app uses to render a status.
 * Used by: every TicketsView / TicketDetailDialog / TicketPanel /
 *       TicketTable / TicketsWidget surface, plus the shared DataTable
 *       `TICKET_STATUS_OPTIONS` registry.
 * Depends on: nothing (intentionally — leaf module).
 *
 * IMPORTANT — single source of truth. The backend has a parallel file at
 * `backend/src/constants/ticketStatuses.ts` that must stay in lockstep.
 * The two trees can't share modules directly (separate npm packages), so
 * any change here gets mirrored there. The DB CHECK in `database.ts` is
 * the third pillar — change either side and you've drifted.
 *
 * Design reference: `refactor/wave-w-tickets-model.md` §3 (enum), §11 (W2).
 */

export interface TicketStatusOption {
  value: TicketStatus;
  label: string;
  /** MUI color hex; mirrors `STATUS_COLORS` in tableTheme.ts. */
  color: string;
}

/** Canonical 9-value status enum, in natural lifecycle order.
 *  A3 promoted `invalid` to a first-class terminal value. The gray
 *  `#757575` matches the Design E "invalid pill" treatment (cooler /
 *  more muted than closed_unresolved's neutral gray). */
export const TICKET_STATUSES: readonly TicketStatusOption[] = [
  { value: 'new',                  label: 'New',                   color: '#2196f3' },
  { value: 'assigned',             label: 'Assigned',              color: '#42a5f5' },
  { value: 'working',              label: 'Working',               color: '#ff9800' },
  { value: 'waiting_on_client',    label: 'Waiting on Client',     color: '#9c27b0' },
  { value: 'waiting_on_external',  label: 'Waiting on External',   color: '#ab47bc' },
  { value: 'sent_resolution',      label: 'Sent Resolution',       color: '#26a69a' },
  { value: 'resolved',             label: 'Resolved',              color: '#4caf50' },
  { value: 'closed_unresolved',    label: 'Closed (Unresolved)',   color: '#9e9e9e' },
  { value: 'invalid',              label: 'Invalid',               color: '#757575' },
] as const;

/** String-literal union of the 9 canonical status values. */
export type TicketStatus =
  | 'new'
  | 'assigned'
  | 'working'
  | 'waiting_on_client'
  | 'waiting_on_external'
  | 'sent_resolution'
  | 'resolved'
  | 'closed_unresolved'
  | 'invalid';

/** Just the value strings — convenient for filter sets, picker mappings. */
export const TICKET_STATUS_VALUES: readonly TicketStatus[] =
  TICKET_STATUSES.map(s => s.value);

/** Terminal statuses — the work is done (whether successfully or not).
 *  `invalid` joined the set in A3. The decay column (Operations Tickets
 *  table) suppresses the freshness dot for terminal-status tickets. */
export const TERMINAL_STATUSES: readonly TicketStatus[] = [
  'resolved',
  'closed_unresolved',
  'invalid',
];

/** Non-terminal statuses — ticket is still in flight. */
export const ACTIVE_STATUSES: readonly TicketStatus[] =
  TICKET_STATUS_VALUES.filter(v => !TERMINAL_STATUSES.includes(v));

/**
 * Human-readable label for a status value. Falls back to the raw value
 * (replacing underscores with spaces) when the value isn't canonical —
 * so a legacy string still renders something readable rather than crashing.
 */
export function statusLabel(value: string | null | undefined): string {
  if (!value) return '';
  const entry = TICKET_STATUSES.find(s => s.value === value);
  if (entry) return entry.label;
  return String(value).replace(/_/g, ' ');
}

/**
 * Type guard: is this string a canonical TicketStatus?
 */
export function isTicketStatus(value: unknown): value is TicketStatus {
  return typeof value === 'string'
    && (TICKET_STATUS_VALUES as readonly string[]).includes(value);
}

/**
 * Color for a status value. Falls back to a neutral gray for unknowns.
 */
export function statusColor(value: string | null | undefined): string {
  if (!value) return '#9e9e9e';
  const entry = TICKET_STATUSES.find(s => s.value === value);
  return entry?.color ?? '#9e9e9e';
}
